import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import { Select, SingleValue } from 'chakra-react-select';
import { useMemo, useState } from 'react';

import { nth } from '@duke-hq/lib';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { testIds } from './testIds';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

type Props = {
  assessmentSubmission: RouterOutputs['assessments']['getById'];
};

export function OverallFeedback({ assessmentSubmission }: Props) {
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | undefined>(
    assessmentSubmission?.id,
  );

  const submissionOptions = useMemo(() => {
    if (assessmentSubmission === undefined || assessmentSubmission === null) {
      return [];
    }
    const allSubmissions = [
      assessmentSubmission,
      ...(assessmentSubmission.previousAssessmentSubmissions ?? []),
    ];
    const totalSubmissions = allSubmissions.length;

    return allSubmissions
      .filter((submission) => submission.id !== undefined)
      .map((submission, index) => ({
        value: submission.id,
        label: `${nth(totalSubmissions - index)} Submission`,
        feedback: submission.assessment_feedback,
        assessedBy: `${submission.assessedBy?.first_name} ${submission.assessedBy?.last_name}`,
      }));
  }, [assessmentSubmission]);

  const selectedOption = useMemo(
    () => submissionOptions.find((option) => option.value === selectedSubmissionId),
    [submissionOptions, selectedSubmissionId],
  );

  if (!selectedOption) {
    return null;
  }

  const hasMultipleSubmissions = submissionOptions.length > 1;

  return (
    <Box data-testid={testIds.previousAttemptsTable} py={4} px={7} width="full">
      <Divider mb={4} />

      {hasMultipleSubmissions ? (
        <Select
          options={submissionOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          value={{ value: selectedOption.value, label: selectedOption.label }}
          onChange={(option: SingleValue<{ value: number | undefined; label: string }>) =>
            setSelectedSubmissionId(option?.value)
          }
          isSearchable={false}
        />
      ) : (
        <Text fontWeight="medium">
          <Trans>Overall Feedback</Trans>
        </Text>
      )}

      <VStack gap={2.5} alignItems="start" mt={4}>
        <Flex justifyContent="space-between" w="100%">
          <Text fontSize="sm" fontWeight="medium">
            Feedback:
          </Text>
          {hasMultipleSubmissions ? (
            <Text fontSize="sm" as="i">
              {`${selectedOption.assessedBy}`}
            </Text>
          ) : null}
        </Flex>
        <Text fontSize="sm">{selectedOption.feedback ?? t`No feedback provided.`}</Text>
      </VStack>
    </Box>
  );
}
